/**
 * Pending incoming friend-request counts (`count` on the incoming-requests and
 * dedicated count endpoints) are capped at this value. A returned `count` equal
 * to {@link FRIEND_REQUEST_COUNT_CAP} means "at least this many" — clients
 * should render it as `${FRIEND_REQUEST_COUNT_CAP}+`.
 *
 * The cap lets the server bound the count query so an inflated pending-request
 * backlog can never trigger a full scan, while staying lossless for the badge,
 * which only distinguishes "some" from "a lot".
 */
export const FRIEND_REQUEST_COUNT_CAP = 99;
export class FriendsApi {
    client;
    constructor(client) {
        this.client = client;
    }
    /**
     * Send a friend request.
     *
     * @param identityId Target identity to send request to
     * @param captchaResponse Optional FriendlyCaptcha response token (required for free-tier users)
     */
    async sendRequest(identityId, captchaResponse) {
        const body = { identityId };
        if (captchaResponse) {
            body['frc-captcha-response'] = captchaResponse;
        }
        return this.client.post('/api/friends/requests', body);
    }
    /**
     * Accept a friend request.
     */
    async acceptRequest(requestId) {
        return this.client.post(`/api/friends/requests/${encodeURIComponent(requestId)}/accept`, {});
    }
    /**
     * Ignore a friend request.
     */
    async ignoreRequest(requestId) {
        return this.client.post(`/api/friends/requests/${encodeURIComponent(requestId)}/ignore`, {});
    }
    /**
     * Cancel an outgoing friend request.
     */
    async cancelRequest(requestId) {
        return this.client.delete(`/api/friends/requests/${encodeURIComponent(requestId)}`);
    }
    /**
     * Get incoming friend requests.
     */
    async getIncomingRequests(limit, cursor) {
        const params = new URLSearchParams();
        if (limit)
            params.set('limit', limit.toString());
        if (cursor)
            params.set('cursor', cursor);
        const query = params.toString();
        return this.client.get(`/api/friends/requests/incoming${query ? `?${query}` : ''}`);
    }
    /**
     * Get outgoing friend requests.
     */
    async getOutgoingRequests(limit, cursor) {
        const params = new URLSearchParams();
        if (limit)
            params.set('limit', limit.toString());
        if (cursor)
            params.set('cursor', cursor);
        const query = params.toString();
        return this.client.get(`/api/friends/requests/outgoing${query ? `?${query}` : ''}`);
    }
    /**
     * Get pending incoming request count.
     */
    async getIncomingRequestCount() {
        return this.client.get('/api/friends/requests/count');
    }
    /**
     * Get friends list (paginated).
     */
    async getFriends(limit, cursor) {
        const params = new URLSearchParams();
        if (limit)
            params.set('limit', limit.toString());
        if (cursor)
            params.set('cursor', cursor);
        const query = params.toString();
        return this.client.get(`/api/friends${query ? `?${query}` : ''}`);
    }
    /**
     * Search friends by username/displayName.
     */
    async searchFriends(query, limit) {
        const params = new URLSearchParams({ q: query });
        if (limit)
            params.set('limit', limit.toString());
        return this.client.get(`/api/friends/search?${params.toString()}`);
    }
    /**
     * Remove a friend.
     */
    async removeFriend(identityId) {
        return this.client.delete(`/api/friends/${encodeURIComponent(identityId)}`);
    }
    /**
     * Get friendship status with an identity.
     */
    async getFriendshipStatus(identityId) {
        return this.client.get(`/api/friends/status/${encodeURIComponent(identityId)}`);
    }
}
//# sourceMappingURL=friends-api.js.map